/**
 * AI Assist Chat Page (Web)
 * Chat interface for AI-powered trading assistance using AG-UI Protocol
 * 
 * ⚠️ IMPORTANT: Per ADR-FE-001, this component only handles UI and user interactions.
 * All AI logic and processing is handled by backend ai-assist-service (AG-UI server).
 * 
 * Uses AG-UI client (AGUIProvider + useAGUIChat) for backend communication.
 */

import { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useDrop, DropTargetMonitor } from 'react-dnd';
import {
  Box,
  Typography,
  TextField,
  IconButton,
  CircularProgress,
  Chip,
} from '@mui/material';
import {
  Send as SendIcon,
} from '@mui/icons-material';
import { AGUIProvider, useAGUIChat } from '@flowrithm/shared/src/features/ai-assist';
import { 
  createSession, 
  getShortSessionId, 
  getStoredSessionId, 
  storeSessionId,
  getSessionMessages,
  type MessageEvent
} from '@flowrithm/shared/src/features/ai-assist/services/sessionService';
import { Message, UserMessage, AssistantMessage } from '@flowrithm/shared/src/features/ai-assist/types/agui';
import { AuthenticatedLayout } from '../../components/layout/AuthenticatedLayout';
import { DraggedInstrument, INSTRUMENT_ITEM_TYPE } from '../../components/instruments/DraggableInstrumentItem';
import { ChatMessage } from '@flowrithm/shared/src/features/ai-assist/components/ChatMessage';
import { normalizeDGUIContent } from '@flowrithm/shared/src/features/ai-assist/utils/normalizeDGUIContent';
import { SessionsList, type SessionsListRef } from './components/SessionsList';
import { DGUIRenderer } from './components/DGUIRenderer';
import { AIAssistAvatarIcon } from './components/AIAssistAvatarIcon';

/**
 * Main page content component (inside AGUIProvider)
 */
function AIAssistPageContent() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [inputValue, setInputValue] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  
  // Ref to prevent duplicate session creation (handles React Strict Mode double-mount)
  const isInitializingRef = useRef(false);
  // Ref to access SessionsList refresh function
  const sessionsListRef = useRef<SessionsListRef>(null);
  
  // Store full ADK session_id in state (not in URL - URL only has short ID)
  // Try to restore from localStorage/sessionStorage first (created on login)
  const [currentSessionId, setCurrentSessionId] = useState<string | null>(() => {
    const stored = getStoredSessionId();
    if (stored) {
      return stored;
    }
    return null;
  });
  const [isCreatingSession, setIsCreatingSession] = useState(false);
  
  // Use AG-UI chat hook - pass full session_id (not thread_id)
  // Note: AG-UI protocol uses 'thread_id' field name, but we put session_id value in it
  // IMPORTANT: Pass session_id to useAGUIChat (can be "pending" initially)
  // If currentSessionId is null, pass "pending" to let ADK create a session automatically
  const { messages, sendMessage, isStreaming, error, loadMessages } = useAGUIChat(currentSessionId || "pending");
  
  /**
   * Convert backend MessageEvent format to AG-UI Message format
   */
  const convertMessageEventToAGUIMessage = (event: MessageEvent): Message | null => {
    try {
      // Normalize author to lowercase for comparison
      const author = (event.author || '').toLowerCase().trim();
      
      if (author === 'user') {
        const userMessage: UserMessage = {
          id: event.id,
          role: 'user',
          content: event.content,
        };
        return userMessage;
      } else if (author === 'assistant' || author === 'agent' || author === 'model' || author === 'system') {
        const content =
          typeof event.content === 'string'
            ? (normalizeDGUIContent(event.content) ?? event.content)
            : event.content;
        const assistantMessage: AssistantMessage = {
          id: event.id,
          role: 'assistant',
          content: content as AssistantMessage['content'],
        };
        return assistantMessage;
      } else {
        // Log the actual author value for debugging
        console.warn('[AIAssistPage] Unknown message author:', event.author);
        const content =
          typeof event.content === 'string'
            ? (normalizeDGUIContent(event.content) ?? event.content)
            : event.content;
        const assistantMessage: AssistantMessage = {
          id: event.id,
          role: 'assistant',
          content: content as AssistantMessage['content'],
        };
        return assistantMessage;
      }
    } catch (err) {
      console.error('[AIAssistPage] Error converting message event:', err instanceof Error ? err.message : 'Unknown error');
      return null;
    }
  };
  

  // Listen for session ID updates from RUN_FINISHED events
  useEffect(() => {
    const handleSessionUpdate = (event: CustomEvent<{ sessionId: string }>) => {
      const newSessionId = event.detail.sessionId;
      if (newSessionId && newSessionId !== currentSessionId && newSessionId !== 'pending') {
        setCurrentSessionId(newSessionId);
        
        // Persist to storage (use localStorage for persistence)
        storeSessionId(newSessionId, true);
        
        // Update URL with short ID for display
        const shortId = getShortSessionId(newSessionId);
        const newParams = new URLSearchParams(searchParams);
        newParams.set('session_id', shortId);
        setSearchParams(newParams, { replace: true });
      }
    };

    if (typeof window !== 'undefined') {
      window.addEventListener('agui-session-updated', handleSessionUpdate as EventListener);
      return () => {
        window.removeEventListener('agui-session-updated', handleSessionUpdate as EventListener);
      };
    }
  }, [currentSessionId, searchParams, setSearchParams]);

  // Initialize session: Create ADK session if not exists, or load messages if session exists
  useEffect(() => {
    const initializeSession = async () => {
      // Prevent duplicate session creation (handles React Strict Mode double-mount)
      if (isInitializingRef.current) {
        return;
      }
      
      // If we already have a session_id in state, load its messages
      if (currentSessionId) {
        try {
          const response = await getSessionMessages(currentSessionId);
          const aguiMessages: Message[] = response.messages
            .map((event) => convertMessageEventToAGUIMessage(event))
            .filter((msg): msg is Message => msg !== null);
          loadMessages(aguiMessages);
        } catch (err) {
          console.error('[AIAssistPage] Failed to load messages for existing session:', err instanceof Error ? err.message : 'Unknown error');
          // Continue - messages will be empty, which is fine for a new session
        }
        return;
      }

      // Mark as initializing to prevent duplicate calls
      isInitializingRef.current = true;
      
      try {
        setIsCreatingSession(true);
        const response = await createSession();
        const fullSessionId = response.session_id;
        
        // Store full session_id in state
        setCurrentSessionId(fullSessionId);
        
        // Persist to storage (use localStorage for persistence)
        storeSessionId(fullSessionId, true);
        
        // Update URL with short ID for display
        const shortId = getShortSessionId(fullSessionId);
      const newParams = new URLSearchParams(searchParams);
        newParams.set('session_id', shortId);
      setSearchParams(newParams, { replace: true });
        
        // Refresh sessions list to show the new session
        sessionsListRef.current?.refresh();
      } catch (err) {
        console.error('[AIAssistPage] Failed to create session:', err instanceof Error ? err.message : 'Unknown error');
        // TODO: Show error to user
      } finally {
        setIsCreatingSession(false);
        isInitializingRef.current = false;
      }
    };

    initializeSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount - we check currentSessionId inside the effect

  // Log errors if any
  useEffect(() => {
    if (error) {
      console.error('AG-UI chat error:', error instanceof Error ? error.message : String(error));
    }
  }, [error]);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Handle instrument from URL params
  useEffect(() => {
    const instrumentParam = searchParams.get('instrument');

    if (instrumentParam && currentSessionId) {
      // Send the instrument as a message
      sendMessage(instrumentParam, currentSessionId);
      
      // Clear the URL param
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('instrument');
      newParams.delete('instrumentId');
      setSearchParams(newParams, { replace: true });
    }
  }, [searchParams, setSearchParams, sendMessage, currentSessionId]);

  // Drag and drop handler for instruments
  const [{ isOver }, drop] = useDrop(
    () => ({
      accept: INSTRUMENT_ITEM_TYPE,
      drop: (item: DraggedInstrument) => {
        const symbolText = item.exchange ? `${item.symbol} (${item.exchange})` : item.symbol;
        if (currentSessionId) {
          // Send the instrument symbol as a message
          sendMessage(symbolText, currentSessionId);
        }
      },
      collect: (monitor: DropTargetMonitor) => ({
        isOver: monitor.isOver(),
      }),
    }),
    [sendMessage, currentSessionId]
  );

  const handleSend = async () => {
    if (!inputValue.trim() || isStreaming || !currentSessionId) {
      return;
    }

    const messageText = inputValue.trim();
    setInputValue('');

    try {
      await sendMessage(messageText, currentSessionId);
    } catch (err) {
      console.error('[AIAssistPage] Failed to send message:', err instanceof Error ? err.message : 'Unknown error');
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleNewChat = async () => {
    // Create new ADK session for new conversation
    try {
      setIsCreatingSession(true);
      
      // Clear messages immediately to show empty chat interface
      loadMessages([]);
      
      const response = await createSession();
      const fullSessionId = response.session_id;
      
      // Store full session_id in state
      setCurrentSessionId(fullSessionId);
      
      // Persist to storage (use localStorage for persistence)
      storeSessionId(fullSessionId, true);
      
      // Update URL with short ID for display
      const shortId = getShortSessionId(fullSessionId);
      const newParams = new URLSearchParams(searchParams);
      newParams.set('session_id', shortId);
      setSearchParams(newParams);
      setInputValue('');
      
      // Refresh sessions list to show the new session
      sessionsListRef.current?.refresh();
    } catch (err) {
      console.error('[AIAssistPage] Failed to create new session:', err instanceof Error ? err.message : 'Unknown error');
      // TODO: Show error to user
    } finally {
      setIsCreatingSession(false);
    }
  };

  const handleQuickAction = async (action: string) => {
    if (!currentSessionId) {
      return;
    }
    
    try {
      await sendMessage(action, currentSessionId);
    } catch (err) {
      console.error('Failed to send quick action:', err instanceof Error ? err.message : 'Unknown error');
    }
  };

  const handleSessionSelect = async (sessionId: string) => {
    // Update session ID immediately
    setCurrentSessionId(sessionId);
    
    // Update URL with short ID for display
    const shortId = getShortSessionId(sessionId);
    const newParams = new URLSearchParams(searchParams);
    newParams.set('session_id', shortId);
    setSearchParams(newParams, { replace: true });
    
    // Fetch and load messages for the selected session
    try {
      const response = await getSessionMessages(sessionId);
      
      // Convert backend message events to AG-UI Message format
      const aguiMessages: Message[] = response.messages
        .map(convertMessageEventToAGUIMessage)
        .filter((msg): msg is Message => msg !== null);
      
      // Load messages into the chat
      loadMessages(aguiMessages);
    } catch (err) {
      console.error('[AIAssistPage] Failed to fetch session messages:', err instanceof Error ? err.message : 'Unknown error');
      // Clear messages on error to show empty state
      loadMessages([]);
      // TODO: Show error to user
    }
  };

  return (
    <AuthenticatedLayout>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          height: 'calc(100vh - 200px)',
          width: '100%',
          maxWidth: 'none', // Override parent maxWidth constraint
          position: 'relative',
          ml: { xs: 0, md: -2 },
          mr: { xs: 0, md: -2 }, // Extend to right edge
        }}
      >
        {/* Sessions List on the Left */}
        <Box
          sx={{
            display: { xs: 'none', md: 'flex' }, // Hide on mobile, show on desktop
            flex: '0 0 auto', // Don't grow or shrink, use content width
            mr: 2, // Add margin to the right for spacing
            ml: { md: -5 }, // Move further left on desktop
            mt: { md: -3 }, // Move upwards on desktop (adjusted down slightly)
          }}
        >
          <SessionsList
            ref={sessionsListRef}
            currentSessionId={currentSessionId}
            onSessionSelect={handleSessionSelect}
            onNewChat={handleNewChat}
            isCreatingSession={isCreatingSession}
            width={250}
          />
        </Box>

        {/* Main Chat Area */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            minWidth: 0, // Allow flex item to shrink below content size
          }}
        >
        {/* Messages Area */}
        <Box
          ref={drop}
          sx={{
            flex: 1,
            overflowY: 'auto',
            px: { xs: 2, md: 4 },
            py: 2,
            display: 'flex',
            flexDirection: 'column',
            position: 'relative',
            '&::before': isOver
              ? {
                  content: '""',
                  position: 'absolute',
                  inset: 0,
                  border: '2px dashed',
                  borderColor: '#04377b',
                  backgroundColor: 'rgba(4, 55, 123, 0.1)',
                  zIndex: 1,
                  pointerEvents: 'none',
                  borderRadius: '8px',
                }
              : {},
            '&::-webkit-scrollbar': {
              width: '8px',
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: 'transparent',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: 'rgba(4, 55, 123, 0.2)',
              borderRadius: '4px',
              '&:hover': {
                backgroundColor: 'rgba(4, 55, 123, 0.3)',
              },
            },
          }}
        >
          {messages.length === 0 && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                textAlign: 'center',
                color: 'text.secondary',
              }}
            >
              <Typography variant="h6" sx={{ mb: 1, color: '#04377b' }}>
                Hello! I'm your AI trading assistant.
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(4, 55, 123, 0.7)' }}>
                How can I help you today?
              </Typography>
            </Box>
          )}

          {messages.map((message) => (
            <ChatMessage
              key={message.id}
              message={message}
              isStreaming={false}
              renderDgui={(schema) => <DGUIRenderer schema={schema} />}
              assistantAvatar={<AIAssistAvatarIcon />}
            />
          ))}

          {isStreaming && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 2 }}>
              <CircularProgress size={24} />
            </Box>
          )}

          <div ref={messagesEndRef} />
        </Box>

        {/* Input Area */}
        <Box
          sx={{
            px: { xs: 2, md: 4 },
            py: 2,
            backgroundColor: 'rgba(255, 255, 255, 0.5)',
            backdropFilter: 'blur(10px)',
            borderTop: '1px solid rgba(4, 55, 123, 0.1)',
          }}
        >
          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-end', mb: 1.5 }}>
            <Box
              sx={{
                flex: 1,
                position: 'relative',
              }}
            >
              <TextField
                inputRef={inputRef}
                fullWidth
                multiline
                maxRows={4}
                placeholder="Ask me anything about trading, portfolio, or market analysis... (Drag instruments from sidebar)"
                value={inputValue}
                onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                  setInputValue(e.target.value)
                }
                onKeyPress={handleKeyPress}
                disabled={isStreaming}
                variant="outlined"
                size="small"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: isOver ? 'rgba(255, 255, 255, 0.95)' : 'rgba(255, 255, 255, 0.8)',
                    backdropFilter: 'blur(5px)',
                    borderRadius: '24px',
                    transition: 'all 0.2s ease',
                    '& fieldset': {
                      borderColor: isOver ? '#04377b' : 'rgba(4, 55, 123, 0.2)',
                      borderWidth: isOver ? 2 : 1,
                    },
                    '&:hover fieldset': {
                      borderColor: isOver ? '#04377b' : 'rgba(4, 55, 123, 0.3)',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#04377b',
                    },
                  },
                }}
              />
            </Box>
            <IconButton
              color="primary"
              onClick={handleSend}
              disabled={!inputValue.trim() || isStreaming}
              sx={{
                bgcolor: '#04377b',
                color: '#fff',
                width: 44,
                height: 44,
                borderRadius: '50%',
                '&:hover': {
                  bgcolor: '#032e64',
                },
                '&:disabled': {
                  bgcolor: 'rgba(4, 55, 123, 0.3)',
                  color: 'rgba(255, 255, 255, 0.5)',
                },
              }}
            >
              {isStreaming ? <CircularProgress size={18} color="inherit" /> : <SendIcon fontSize="small" />}
            </IconButton>
          </Box>
          
          {/* Quick Action Chips */}
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Chip
              label="Portfolio Analysis"
              size="small"
              onClick={() => handleQuickAction('Analyze my portfolio performance')}
              sx={{
                cursor: 'pointer',
                bgcolor: 'rgba(4, 55, 123, 0.1)',
                color: '#04377b',
                '&:hover': {
                  bgcolor: 'rgba(4, 55, 123, 0.2)',
                },
              }}
            />
            <Chip
              label="Market Insights"
              size="small"
              onClick={() => handleQuickAction('What are the current market trends?')}
              sx={{
                cursor: 'pointer',
                bgcolor: 'rgba(4, 55, 123, 0.1)',
                color: '#04377b',
                '&:hover': {
                  bgcolor: 'rgba(4, 55, 123, 0.2)',
                },
              }}
            />
            <Chip
              label="Trading Strategy"
              size="small"
              onClick={() => handleQuickAction('Suggest a trading strategy for me')}
              sx={{
                cursor: 'pointer',
                bgcolor: 'rgba(4, 55, 123, 0.1)',
                color: '#04377b',
                '&:hover': {
                  bgcolor: 'rgba(4, 55, 123, 0.2)',
                },
              }}
            />
          </Box>
        </Box>
        </Box>
      </Box>
    </AuthenticatedLayout>
  );
}

/**
 * Main exported component
 * Wraps content in AGUIProvider for AG-UI protocol support
 */
export function AIAssistPage() {
  return (
    <AGUIProvider>
      <AIAssistPageContent />
    </AGUIProvider>
  );
}
